import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetAuthInspectQuery, usePostAuthRefreshMutation } from '../../store/backendApi';

const REFRESH_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
const REFRESH_THRESHOLD = 10 * 60; // 10 minutes in seconds

interface UseSilentRefreshReturn {
  lastCheckTime: number | null;
  sessionExpiresAt: number | null;
}

/**
 * Hook to handle automatic session refresh in the background.
 * Platform-agnostic: Works in React Native and web.
 *
 * Checks session status every 5 minutes and refreshes if:
 * - Session expires in less than 10 minutes
 * - Remember me is enabled
 *
 * @returns Debug information about last check time and session expiry
 */
export const useSilentRefresh = (): UseSilentRefreshReturn => {
  const [lastCheckTime, setLastCheckTime] = useState<number | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Use the inspect endpoint to check session status
  const { refetch: refetchInspect } = useGetAuthInspectQuery();

  // Use the refresh endpoint to refresh tokens
  const [refreshToken] = usePostAuthRefreshMutation();

  const checkAndRefreshToken = useCallback(async () => {
    try {
      // Refetch the inspect data to get the latest session information
      const result = await refetchInspect();

      if (result.data?.active) {
        const { sessionExpiresAt, rememberMe } = result.data.active;
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        const timeUntilExpiry = sessionExpiresAt - currentTime;

        // Update state for debugging
        setLastCheckTime(Date.now());
        setSessionExpiresAt(sessionExpiresAt);

        // If session expires in less than threshold AND rememberMe is active, refresh the token
        if (timeUntilExpiry < REFRESH_THRESHOLD && rememberMe) {
          await refreshToken();
        }
      }
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.error('Silent refresh check failed:', error);
    }
  }, [refetchInspect, refreshToken]);

  useEffect(() => {
    // Start the interval to check for token refresh every 5 minutes
    intervalRef.current = setInterval(checkAndRefreshToken, REFRESH_CHECK_INTERVAL);

    // Also check immediately when the component mounts
    checkAndRefreshToken();

    // Cleanup function to clear the interval when component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkAndRefreshToken]);

  return { lastCheckTime, sessionExpiresAt };
};
